<template>
  <f7-page name="ad-dashboard" @page:init="load">
    <f7-navbar :title="$t('ads.avocashAds')" back-link>
      <f7-nav-right>
        <f7-link icon-f7="plus" href="/ads/create" />
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <f7-block class="wallet-strip">
          <div>
            <div class="wallet-label">{{ $t("ads.adBudgetBalance") }}</div>
            <div class="wallet-amount">{{ fmtCfa(wallet.balance) }}</div>
          </div>
          <f7-button small outline @click="topUpOpened = true">{{
            $t("common.topUp")
          }}</f7-button>
        </f7-block>

        <f7-block class="metric-grid">
          <div class="metric">
            <div class="metric-lbl">{{ $t("ads.impressions") }}</div>
            <div class="metric-val">{{ fmt(summary.impressions) }}</div>
          </div>
          <div class="metric">
            <div class="metric-lbl">{{ $t("ads.impressions") }}</div>
            <div class="metric-val">{{ fmt(summary.impressions) }}</div>
          </div>
          <div class="metric">
            <div class="metric-lbl">{{ $t("ads.groupsActivated") }}</div>
            <div class="metric-val">{{ fmt(summary.groupsActivated) }}</div>
          </div>
          <div class="metric">
            <div class="metric-lbl">{{ $t("ads.groupsActivated") }}</div>
            <div class="metric-val">{{ fmt(summary.groupsActivated) }}</div>
          </div>
          <div class="metric">
            <div class="metric-lbl">{{ $t("ads.ctr") }}</div>
            <div class="metric-val">{{ summary.ctr }}%</div>
          </div>
          <div class="metric">
            <div class="metric-lbl">{{ $t("ads.costPerOrder") }}</div>
            <div class="metric-val">{{ fmtCfa(summary.costPerOrder) }}</div>
          </div>
          <div class="metric">
            <div class="metric-lbl">{{ $t("ads.costPerOrder") }}</div>
            <div class="metric-val">{{ fmtCfa(summary.costPerOrder) }}</div>
          </div>
        </f7-block>

        <f7-block-title>{{ $t("ads.spendByCategory") }}</f7-block-title>
        <f7-block>
          <div
            v-for="row in spendByCategory"
            :key="row.code"
            class="segment-row"
          >
            <span class="segment-label">{{ adCategoryLabel(row.code) }}</span>
            <div class="segment-track">
              <div class="segment-fill" :style="{ width: row.pct + '%' }" />
            </div>
            <span class="segment-pct">{{ row.pct }}%</span>
          </div>
        </f7-block>
        <f7-block-title>{{ $t("ads.activeCampaigns") }}</f7-block-title>
        <f7-list inset strong media-list>
          <f7-list-item
            v-for="c in campaigns"
            :key="c.campaignId"
            :title="c.campaignName"
            :subtitle="`${fmtCfa(c.spentToday)} ${$t('common.today')}`"
            link
            :href="`/ads/campaigns/${c.campaignId}`"
          >
            <template #after>
              <div class="camp-status" :class="c.status.toLowerCase()">
                {{ statusLabel(c.status) }}
              </div>
            </template>
            <template #text>
              <div class="camp-track">
                <div class="camp-fill" :style="{ width: budgetPct(c) + '%' }" />
              </div>
            </template>
          </f7-list-item>
        </f7-list>

        <f7-block-title>{{ $t("ads.subscriptions") }}</f7-block-title>
        <f7-list inset strong>
          <f7-list-item
            v-for="s in subscriptions"
            :key="s.id"
            :title="productLabel(s.adProduct)"
            :subtitle="s.status"
            :after="fmtCfa(s.periodicAmount)"
          />
          <f7-list-item
            v-if="!subscriptions.length"
            :title="$t('ads.noActiveSubscriptions')"
          />
        </f7-list>
      </template>
    </AppLayout>

    <!-- Ad budget top-up sheet -->
    <AdaptiveSheet v-model:opened="topUpOpened"  swipe-to-close close-by-outside-click close-on-escape :tablet-fullscreen="false">    
      <f7-page>
      <div class="card-header">
        <div class="text-align-center">
          {{ $t("ads.topUpBudget") }}
        </div>
        <f7-link sheet-close popup-close style="right:8px"><f7-icon ios="f7:multiply" md="material:close" /></f7-link>
      </div>
      <f7-block strong inset>  
        <div class="preset-row grid grid-cols-2 grid-gap">
          <f7-button class="preset-btn"  v-for="preset in presets" :key="preset" outline small round :fill="amount === preset"  @click="amount = preset">
            {{ fmtCfa(preset) }}
          </f7-button>
        </div>
        <f7-list form inset>
          <f7-list-input v-model:value="amount" type="number" min="100" :label="$t('campaign.amountCFA')" placeholder="10000" />
        </f7-list>
        <f7-button large fill round :disabled="!Number(amount)" @click="confirmTopUp">
          {{ $t("common.continue") }} — {{ fmtCfa(Number(amount) || 0) }}
        </f7-button>
      </f7-block>
      </f7-page>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import AppLayout from "../components/common/AppLayout.vue";
import AdaptiveSheet from "../components/common/AdaptiveSheet.vue";
import { reactive, ref } from "vue";
import { f7 } from "framework7-vue";
import client from "../js/api/client.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import { usePaymentTrigger } from "../js/composables/usePaymentTrigger.js";
import { fmtCfa, productMeta } from "../constants/ad-products.js";
import { adCategoryLabel } from "../constants/ad-targeting-categories.js";
import i18n from "../js/i18n.js";
const { t } = i18n.global;

const keycloakStore = useKeycloakStore();
const trigger = usePaymentTrigger({ f7router: null });

const topUpOpened = ref(false);
const amount = ref(null);
const presets = [5000, 10000, 25000, 50000];

function confirmTopUp() {
  try {
    trigger.triggerAdBudgetTopUp({
      advertiserId: keycloakStore.subject,
      amount: Number(amount.value),
    });
    topUpOpened.value = false;
    amount.value = null;
  } catch (err) {
    f7?.dialog?.alert?.(err?.message || t("common.paymentFailed"));
  }
}
const wallet = reactive({ balance: 0 });
const summary = reactive({
  impressions: 0,
  groupsActivated: 0,
  ctr: 0,
  costPerOrder: 0,
});
const spendByCategory = ref([]);
const campaigns = ref([]);
const subscriptions = ref([]);

function fmt(n) {
  return Math.round(n).toLocaleString("fr-FR");
}
function productLabel(code) {
  return productMeta(code)?.label ?? code;
}
function budgetPct(c) {
  return c.totalBudgetCfa
    ? Math.min(100, Math.round((c.totalSpent / c.totalBudget) * 100))
    : 0;
}
const statusLabels = {
  ACTIVE: t("common.active"),
  PAUSED: t("common.paused"),
  EXHAUSTED: t("common.exhausted"),
  DRAFT: t("common.draft"),
  COMPLETED: t("common.complete"),
};
function statusLabel(s) {
  return statusLabels[s] || s;
}

async function load() {
  const [walletRes, statsRes, subsRes] = await Promise.all([
    client.get(`billing/wallets/${keycloakStore.subject}`),
    client.get("adCampaigns/stats", {
      params: { advertiserId: keycloakStore.subject },
    }),
    client
      .get("subscriptions", {
        params: { advertiserId: keycloakStore.subject },
      })
      .catch(() => ({ data: [] })),
  ]);

  wallet.balance = walletRes.data.balance;
  campaigns.value = statsRes.data;
  subscriptions.value = subsRes.data;

  const totalImpr = campaigns.value.reduce((s, c) => s + c.impressions, 0);
  const totalClicks = campaigns.value.reduce((s, c) => s + c.clicks, 0);
  summary.impressions = totalImpr;
  summary.ctr = totalImpr ? ((totalClicks / totalImpr) * 100).toFixed(1) : 0;
  summary.groupsActivated = campaigns.value.reduce(
    (s, c) =>
      s +
      (c.impressionsByAdTargetingCategory
        ? Object.values(c.impressionsByAdTargetingCategory).reduce(
            (a, b) => a + b,
            0,
          )
        : 0),
    0,
  );
  summary.costPerOrder = totalClicks
    ? Math.round(
        campaigns.value.reduce((s, c) => s + Number(c.totalSpent), 0) /
          Math.max(1, totalClicks),
      )
    : 0;

  const agg = {};
  campaigns.value.forEach((c) => {
    Object.entries(c.impressionsByAdTargetingCategory || {}).forEach(
      ([k, v]) => {
        agg[k] = (agg[k] || 0) + v;
      },
    );
  });
  const total = Object.values(agg).reduce((a, b) => a + b, 0) || 1;
  spendByCategory.value = Object.entries(agg)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([code, v]) => ({ code, pct: Math.round((v / total) * 100) }));
}
</script>

<style scoped>
.wallet-strip {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--f7-list-bg-color, #f5f5f5);
  border-radius: 10px;
}
.wallet-label {
  font-size: 11px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.wallet-amount {
  font-size: 18px;
  font-weight: 500;
}
.metric-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.metric {
  background: var(--f7-list-bg-color, #f5f5f5);
  border-radius: 9px;
  padding: 10px;
}
.metric-lbl {
  font-size: 10px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.metric-val {
  font-size: 17px;
  font-weight: 500;
}
.segment-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  font-size: 12px;
}
.segment-label {
  flex: 0 0 40%;
}
.segment-track {
  flex: 1;
  height: 5px;
  border-radius: 3px;
  background: var(--f7-list-item-border-color, #e0e0e0);
  overflow: hidden;
}
.segment-fill {
  height: 100%;
  background: var(--f7-theme-color);
  border-radius: 3px;
}
.segment-pct {
  flex: 0 0 32px;
  text-align: right;
  font-weight: 500;
}
.camp-track {
  height: 4px;
  border-radius: 2px;
  background: var(--f7-list-item-border-color, #e0e0e0);
  margin-top: 4px;
  overflow: hidden;
}
.camp-fill {
  height: 100%;
  background: var(--f7-theme-color);
  border-radius: 2px;
}
.camp-status {
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 6px;
  background: var(--f7-list-bg-color, #f5f5f5);
}
.camp-status.active {
  color: #1a6b42;
}
.camp-status.exhausted,
.camp-status.paused {
  color: #a32d2d;
}
</style>
